import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { httpClient } from "infra/axios/axios-api";
import { Expense } from "domain/entities/expense";
import { ExpenseStatusEnum } from "domain/enum/expenseStatus";
import { MessageTypeEnum } from "domain/enum/messageType";
import Spinner from "presentation/components/Shared/Spinner";
import { useExpenseById, useUpdateExpenseMutation } from "presentation/hooks/use-query-expense";
import { useAuthStore } from "presentation/stores/use-auth-store";
import MobilePDFViewer from "presentation/mobile/components/MobilePDFViewer";

// Tempo para o utilizador ver o resultado antes de o modal fechar 
const CLOSE_DELAY_MS = 1500;

interface MobileExpenseApprovalFormProps {
    expense: Expense;
    onClose: () => void;
    showMessage: (type: MessageTypeEnum, subject: string, message: string) => void;
}

const MobileExpenseApprovalForm: React.FC<MobileExpenseApprovalFormProps> = ({
    expense,
    onClose,
    showMessage,
}) => {
    const { email: userEmployeeEmail } = useAuthStore();
    const { data: expenseData } = useExpenseById(expense.id);
    const { mutateAsync: updateExpense } = useUpdateExpenseMutation();

    const [url, setUrl] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmAction, setConfirmAction] = useState<"approve" | "reject" | null>(null);
    const [visibleRejectionReason, setVisibleRejectionReason] = useState(false);
    const [reasonReject, setReasonReject] = useState("");

    const currentExpense = expenseData?.data ?? expense;
    const canDecide = currentExpense.status === ExpenseStatusEnum.PENDENTE;
    const isPdf = url.toLowerCase().includes(".pdf");

    // Obter o signed URL do comprovativo
    useEffect(() => {
        const fullPath = expense.document?.fullPath;
        if (!fullPath) return;

        let cancelled = false;
        httpClient
            .get(`/assets/${fullPath}`)
            .then((response) => {
                if (!cancelled) setUrl(response.data.url);
            })
            .catch((err) => console.error("Erro ao obter signed URL:", err));

        return () => {
            cancelled = true;
        };
    }, [expense.document?.fullPath]);

    const submitDecision = async (status: ExpenseStatusEnum) => {
        setConfirmAction(null);
        if (!userEmployeeEmail || !canDecide || isSubmitting) return;

        const isApproval = status === ExpenseStatusEnum.APROVADO;
        setIsSubmitting(true);
        setVisibleRejectionReason(false);

        try {
            await updateExpense({
                ...currentExpense,
                status,
                rejectionReason: isApproval ? currentExpense.rejectionReason : reasonReject,
                lastModifiedBy: userEmployeeEmail,
            });

            showMessage(
                MessageTypeEnum.SUCCESS,
                "Workflow",
                isApproval ? "Documento foi aprovado com sucesso" : "Documento foi reprovado com sucesso"
            );
        } catch {
            showMessage(
                MessageTypeEnum.ERROR,
                "Workflow",
                isApproval ? "Erro ao aprovar documento" : "Erro ao reprovar documento"
            );
        }

        setTimeout(onClose, CLOSE_DELAY_MS);
    };


    return (
        <div className="flex flex-column h-full gap-3">
            {/* Comprovativo (PDF via react-pdf ou imagem) */}
            <div className="flex-grow-1 overflow-auto" style={{ minHeight: "350px" }}>
                {!url ? (
                    <div className="flex justify-content-center p-5">
                        <Spinner />
                    </div>
                ) : isPdf ? (
                    <MobilePDFViewer url={url} fileName="Despesa" />
                ) : (
                    <img src={url} alt="Comprovativo da despesa" className="w-full border-round-lg" />
                )}
            </div>

            {/* Ações */}
            {canDecide && (
                <div className="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
                    <Button
                        label="Rejeitar"
                        icon="pi pi-times"
                        severity="danger"
                        outlined
                        size="small"
                        disabled={isSubmitting}
                        onClick={() => {
                            setReasonReject("");
                            setVisibleRejectionReason(true);
                        }}
                    />
                    <Button
                        label={isSubmitting ? "A guardar..." : "Aprovar"}
                        icon={isSubmitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        size="small"
                        disabled={isSubmitting}
                        style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                        onClick={() => setConfirmAction("approve")}
                    />
                </div>
            )}

            <ConfirmDialog
                visible={confirmAction !== null}
                onHide={() => setConfirmAction(null)}
                message={
                    confirmAction === "approve"
                        ? "Deseja aprovar este documento?"
                        : "Deseja rejeitar este documento, sendo este o motivo?"
                }
                header="Confirmação"
                icon="pi pi-exclamation-triangle"
                acceptLabel="Sim"
                rejectLabel="Não"
                accept={() =>
                    submitDecision(
                        confirmAction === "approve"
                            ? ExpenseStatusEnum.APROVADO
                            : ExpenseStatusEnum.NAO_APROVADO
                    )
                }
            />


            {/* Motivo da rejeição */}
            <Dialog
                header="Rejeição do Documento"
                visible={visibleRejectionReason}
                modal
                onHide={() => !isSubmitting && setVisibleRejectionReason(false)}
                className="mx-3"
                style={{ width: "92vw", maxWidth: "420px" }}
            >
                <label htmlFor="motivo-despesa" className="text-xs font-semibold text-700">
                    Informe o Motivo da Rejeição:
                </label>
                <InputTextarea
                    id="motivo-despesa"
                    value={reasonReject}
                    onChange={(e) => setReasonReject(e.target.value)}
                    rows={4}
                    autoFocus
                    maxLength={190}
                    className="w-full mt-1"
                />
                <div className="flex justify-content-end mt-1">
                    <small className="text-500">{reasonReject.length}/190 caracteres</small>
                </div>
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancelar"
                        severity="secondary"
                        outlined
                        size="small"
                        disabled={isSubmitting}
                        onClick={() => setVisibleRejectionReason(false)}
                    />
                    <Button
                        label="Rejeitar"
                        icon="pi pi-check"
                        severity="danger"
                        size="small"
                        disabled={isSubmitting}
                        onClick={() => {
                            if (reasonReject.trim() === "") {
                                showMessage(
                                    MessageTypeEnum.ERROR,
                                    "Erro",
                                    "Informe o motivo da não aprovação do documento."
                                );
                                return;
                            }
                            setConfirmAction("reject");
                        }}
                    />

                </div>
            </Dialog>
        </div>
    );
};

export default MobileExpenseApprovalForm;
